import { get, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { isEmpty } from '@ember/utils';
import { recordIdentifierFor } from '@warp-drive/core';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import type {
  default as Value,
  DynamicValue,
} from '@linc-technologies/linced-common/models/dynamic-field/value';
import type StoreService from '@linc-technologies/linced-common/services/store';
import {
  getValueDefault,
  getValueKey,
} from '@linc-technologies/linced-common/utils/dynamic-field-types';

// These functions hold the dynamic fields code that the DynamicFieldsModel
// and DynamicFieldsFragment base classes share.

// FieldIndex holds each dynamic field value fragment, by field key
// (`namespace:key`). Only the fields are own props, so Object.keys gives the
// field keys. get and set take a path, as EmberObject did:
// `fields.get('core:firstName.value')`.
export class FieldIndex {
  get(path: string): unknown {
    return get(this, path);
  }

  set<T>(path: string, value: T): T {
    return set(this, path, value);
  }
}

// DynamicFieldMap is a FieldIndex of the fields of one record or fragment.
export type DynamicFieldMap = FieldIndex &
  Partial<Record<`${string}:${string}`, Value>>;

// DynamicFieldProps are the props of a new dynamic-field/value fragment.
export type DynamicFieldProps = Partial<Value> & { key: string };

// DynamicFieldsHost is a record or fragment that holds dynamic fields.
export interface DynamicFieldsHost {
  fieldsArray: FragmentArray<Value>;
  store: unknown;
}

// buildFieldMap gives an index of the fragments in fieldsArray by field key.
// The index holds the fragments, so values are not copied back and forth.
export function buildFieldMap(
  fieldsArray: FragmentArray<Value>,
): DynamicFieldMap {
  const map = new FieldIndex() as DynamicFieldMap & Record<string, Value>;
  for (const field of fieldsArray) {
    // Schema `default` values have no key. JS gives them the key 'undefined'.
    map[String(field.key)] = field;
  }
  return map;
}

// fieldProps gives the props of a new field, with the zero value of its
// datatype.
export function fieldProps(
  key: string,
  datatype: number,
  requiredRole?: string,
): DynamicFieldProps {
  const field: Record<string, unknown> = { key, datatype, requiredRole };
  field[getValueKey(datatype)] = getValueDefault(datatype);
  return field as DynamicFieldProps;
}

// addFieldTo adds a new dynamic-field/value fragment to the host fieldsArray.
// If the host has a field with the same key, the new field replaces it. The
// latest field wins, as it did when both were kept, but the key is not
// serialized twice. It scans fieldsArray, not the fields index, so a loop of
// adds does not rebuild the index each time.
export function addFieldTo(
  host: DynamicFieldsHost,
  field: DynamicFieldProps | undefined,
): void {
  const key = field?.key;
  if (key !== undefined) {
    dropDraft(host, key);
    for (const existing of host.fieldsArray) {
      if (existing.key === key) {
        host.fieldsArray.removeFragment(existing);
        break;
      }
    }
  }
  // The runtime store is services/store, a FragmentStore. Model types it as
  // the base store, which has no createFragment.
  const store = host.store as StoreService;
  host.fieldsArray.addFragment(
    store.createFragment('dynamic-field/value', field) as Value,
  );
}

// A draft is a field that a form shows, but that its host does not hold yet.
// It is a value fragment that is not in fieldsArray, so it does not make the
// host dirty. A user edit attaches it. A save attaches each pending draft
// (a draft with a default value), so that the API gets the default.
interface Draft {
  field: Value;
  pending: boolean;
  // base is the value of the host field when the draft was made, or undefined
  // when the host had no such field. A save attaches the draft only when the
  // host field still has this value.
  base: string | undefined;
}

const drafts = new WeakMap<DynamicFieldsHost, Map<string, Draft>>();

// pendingDrafts counts the pending drafts, so that a save does not search for
// drafts when there are none.
let pendingDrafts = 0;

// draftFieldOf gives the draft of a field. It makes the draft on the first
// call, with the given value. A draft with a value other than the zero value
// is pending.
export function draftFieldOf(
  host: DynamicFieldsHost,
  key: string,
  datatype: number,
  value: unknown,
  requiredRole?: string,
): Value {
  let hostDrafts = drafts.get(host);
  if (!hostDrafts) {
    hostDrafts = new Map();
    drafts.set(host, hostDrafts);
  }

  let draft = hostDrafts.get(key);
  if (!draft) {
    const field = fieldProps(key, datatype, requiredRole) as Record<
      string,
      unknown
    >;

    const pending = !isZeroValue(value, datatype);
    if (pending) {
      field[getValueKey(datatype)] = value;
      pendingDrafts++;
    }

    const store = host.store as StoreService;
    draft = {
      field: store.createFragment('dynamic-field/value', field) as Value,
      pending,
      base: snapshotOf(host, key),
    };
    hostDrafts.set(key, draft);
  } else if (!draft.pending && !isZeroValue(draft.field.value, datatype)) {
    // A form shows the field again after a rollback.
    draft.pending = true;
    pendingDrafts++;
  }

  return draft.field;
}

// isDraft is true when the field is a draft of the host.
export function isDraft(host: DynamicFieldsHost, field: Value): boolean {
  return drafts.get(host)?.get(field.key ?? '')?.field === field;
}

// dropDraft removes the draft of a field, when the host gets a real field
// with the same key.
function dropDraft(host: DynamicFieldsHost, key: string): void {
  const hostDrafts = drafts.get(host);
  const draft = hostDrafts?.get(key);
  if (draft) {
    hostDrafts!.delete(key);
    if (draft.pending) {
      pendingDrafts--;
    }
  }
}

// attachDraft adds the draft of a field to the host fieldsArray. It replaces a
// field with the same key, as addFieldTo does.
export function attachDraft(host: DynamicFieldsHost, key: string): void {
  const hostDrafts = drafts.get(host);
  const draft = hostDrafts?.get(key);
  if (!draft) {
    return;
  }

  dropDraft(host, key);
  for (const existing of host.fieldsArray) {
    if (existing.key === key) {
      host.fieldsArray.removeFragment(existing);
      break;
    }
  }

  host.fieldsArray.addFragment(draft.field);
}

// snapshotOf gives the value of a host field as JSON, or undefined when the
// host has no such field.
function snapshotOf(host: DynamicFieldsHost, key: string): string | undefined {
  for (const field of host.fieldsArray) {
    if (field.key === key) {
      const value: unknown = field.value;
      // A list value is a StatefulArray. Copy it to a plain array for JSON.
      return JSON.stringify(
        typeof value === 'object' && value !== null && Symbol.iterator in value
          ? [...(value as Iterable<unknown>)]
          : value,
      );
    }
  }
  return undefined;
}

// settled holds the fields that a user or a caller set. A form shows such a
// field as it is, also when it is empty: it does not show a draft with the
// default in its place.
const settled = new WeakMap<DynamicFieldsHost, Set<string>>();

// settleField records that a user or a caller set the field.
export function settleField(host: DynamicFieldsHost, key: string): void {
  let keys = settled.get(host);
  if (!keys) {
    keys = new Set();
    settled.set(host, keys);
  }
  keys.add(key);
}

// isSettled is true when a user or a caller set the field.
export function isSettled(host: DynamicFieldsHost, key: string): boolean {
  return settled.get(host)?.has(key) ?? false;
}

// attachPendingDrafts attaches the pending drafts of a record and of its
// fragments. The store calls it before each save.
export function attachPendingDrafts(store: StoreService, record: object): void {
  if (pendingDrafts > 0) {
    for (const host of draftHostsIn(store, record)) {
      for (const [key, draft] of drafts.get(host) ?? []) {
        if (!draft.pending) {
          continue;
        }
        if (snapshotOf(host, key) !== draft.base) {
          // A caller set the field after the form made the draft. Keep that
          // value, not the default.
          dropDraft(host, key);
        } else {
          attachDraft(host, key);
        }
      }
    }
  }
}

// cancelPendingDrafts stops the save of the defaults of a record and of its
// fragments. A rollback calls it. The drafts stay, so a form that still shows
// them works, and a form that shows them again makes them pending again. The
// settled fields are no longer settled, because the rollback removes the
// values that the user or the caller set. So a form shows the default again.
export function cancelPendingDrafts(store: StoreService, host: object): void {
  for (const draftHost of draftHostsIn(store, host)) {
    settled.delete(draftHost);
    for (const draft of drafts.get(draftHost)?.values() ?? []) {
      if (draft.pending) {
        draft.pending = false;
        pendingDrafts--;
      }
    }
  }
}

// draftHostsIn gives the host and each fragment in it that has drafts or
// settled fields.
function* draftHostsIn(
  store: StoreService,
  host: object,
): Generator<DynamicFieldsHost> {
  if (
    drafts.has(host as DynamicFieldsHost) ||
    settled.has(host as DynamicFieldsHost)
  ) {
    yield host as DynamicFieldsHost;
  }

  // TS-STANDIN(store): the common store service is JS, so schema is any.
  const schema = (
    store as unknown as {
      schema: { fields(key: object): Map<string, unknown> };
    }
  ).schema;

  for (const [name, field] of schema.fields(recordIdentifierFor(host))) {
    const options = (
      field as { options?: { isFragment?: boolean; fragmentKind?: string } }
    ).options;
    const kind = options?.isFragment ? options.fragmentKind : undefined;
    // fieldsArray holds value fragments, which never hold drafts.
    if (!kind || name === 'fieldsArray') {
      continue;
    }

    const value: unknown = get(host, name);
    if (kind === 'fragment' && value) {
      yield* draftHostsIn(store, value);
    } else if (kind === 'fragment-array' && value) {
      for (const fragment of value as Iterable<object>) {
        yield* draftHostsIn(store, fragment);
      }
    }
  }
}

function isZeroValue(value: unknown, datatype: number): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  const zero = getValueDefault(datatype);
  if (Array.isArray(zero)) {
    return (value as { length?: number }).length === 0;
  }
  return value === zero;
}

// FieldsOwner is a record or fragment with the dynamic fields API.
interface FieldsOwner {
  fields: DynamicFieldMap;
  addField(key: string, datatype: number, requiredRole?: string): void;
}

// fieldOf gives the value fragment of a field, or undefined.
export function fieldOf(
  owner: Pick<FieldsOwner, 'fields'>,
  key: string,
): Value | undefined {
  return (owner.fields as unknown as Record<string, Value | undefined>)[key];
}

// hasSavedValue tells if a field has a value that the owner has saved. An
// edit that is not saved yet does not count, so a form can keep the field
// while the user types in it. A field the user added has no saved value.
export function hasSavedValue(
  owner: Pick<FieldsOwner, 'fields'> & { hasDirtyAttributes: boolean },
  key: string,
): boolean {
  const field = fieldOf(owner, key);
  if (!field) {
    return false;
  }
  // A field can be dirty only while its owner is dirty. Read the owner first:
  // its state is tracked, so a save or a rollback updates the callers.
  if (owner.hasDirtyAttributes && field.hasDirtyAttributes) {
    const change = field.changedAttributes()[getValueKey(field.datatype)];
    if (change) {
      return !isEmpty(change[0]);
    }
  }
  return !isEmpty(field.value);
}

// setFieldValueOn sets the value of a field. If the field is missing, it adds
// the field with the given datatype first, so a key is never added twice.
export function setFieldValueOn(
  owner: FieldsOwner,
  key: string,
  value: unknown,
  datatype?: number,
): void {
  let field = fieldOf(owner, key);
  if (!field) {
    if (datatype === undefined) {
      throw new Error(
        `The dynamic field ${key} is missing. Give a datatype to add it.`,
      );
    }
    owner.addField(key, datatype);
    field = fieldOf(owner, key)!;
  }
  set(field, 'value', value as DynamicValue);
  // The real value replaces a draft that a form made for the field.
  dropDraft(owner as unknown as DynamicFieldsHost, key);
  settleField(owner as unknown as DynamicFieldsHost, key);
}

// dynamicField gives a prop that reads and writes the value of one dynamic
// field. It is undefined when the record has no such field. A write adds the
// field when the decorator has a datatype. Classic computed properties can
// use the prop as a dependent key.
//
// Usage: @dynamicField('core:dob', DataType.STRING) declare dob: string;
// TypeScript types a field decorator as returning void. Ember legacy
// decorators return the descriptor, so the function is cast.
export function dynamicField(
  key: string,
  datatype?: number,
): PropertyDecorator {
  return function (target: object, prop: string): PropertyDescriptor {
    return dependentKeyCompat(target, prop, {
      configurable: true,
      enumerable: true,
      get(this: FieldsOwner): DynamicValue | undefined {
        return fieldOf(this, key)?.value;
      },
      set(this: FieldsOwner, value: unknown) {
        setFieldValueOn(this, key, value, datatype);
      },
    });
  } as unknown as PropertyDecorator;
}
